import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { FIELD_REQUIRED, LOADING } from "../../../../i18n/keysTranslations";
import { Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetOfflineEvents } from "../../../../actions/adsCampaigns";
import { TIKTOK } from "../../../../utils/constants";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";

import { LaunchError } from "../../../../utils/errors";

const SelectOfflineEventsTikTok = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  onChangeAction = () => {},
  integrationID,
  adAccountID,
  appID,
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  // TODO: Call endpoint to get offline events
  const onGetOfflineEvents = async () => {
    setLoading(true);
    try {
      const response = await dispatch(
        startGetOfflineEvents({
          integrationID,
          adAccountID,
          appID,
          platform: TIKTOK,
        })
      );

      if (response.ok) {
        const data = response.data;
        if (data?.length > 0) {
          setOptions(data);
        }
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onGetOfflineEvents",
          capture: "exception",
        })
      );
      console.error("Error fetching offline events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onGetOfflineEvents();
    // eslint-disable-next-line
  }, []);

  const getFilteredOptions = () => {
    return options;
  };

  const filteredOptions = getFilteredOptions();

  const getOption = (option) => {
    const optionValue = options.find((opt) => opt.id === option);
    return optionValue ? optionValue : { id: option, name: option };
  };

  return (
    <Controller
      defaultValue={[]}
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        return (
          <Autocomplete
            {...field}
            value={field.value || []}
            id={name}
            hasLabel={Boolean(label)}
            fullWidth={fullWidth}
            disabled={disabled}
            multiple
            loadingText={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={20} />
                {t(LOADING)}
              </Box>
            }
            loading={loading}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={(_, value) => {
              const selectedIds = value.map((item) => item.id);
              field.onChange(selectedIds);
              onChangeAction(value);
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                const optionValue = filteredOptions.find(
                  (opt) => opt.id === option
                );
                return optionValue ? optionValue.name : option;
              }
              return option?.name || "";
            }}
            options={filteredOptions}
            renderOption={(props, option) => {
              return (
                <Box
                  {...props}
                  component="li"
                  key={option.id}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const optionValue = getOption(option);
                return (
                  <Chip
                    key={option.id}
                    label={optionValue.name}
                    {...getTagProps({ index })}
                    size="small"
                    sx={{
                      backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant={variant}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                error={!!error}
                hiddenLabel={!label}
                helperText={
                  <Box
                    component="span"
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>{!!error ? error?.message : helperText}</span>
                  </Box>
                }
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ...sxTextField,
                  ".MuiFormHelperText-root": {
                    ml: "0 !important",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        );
      }}
    />
  );
};

export default SelectOfflineEventsTikTok;
